/**
 * 品题复习负担统计（首页 / 品题列表共用）。
 * 独立模块，避免与 ttqDisplay 形成循环依赖导致 Metro 导出未就绪。
 */

type BurdenParticleEntry = { particleId: string };

export type BurdenSessionSource = {
  particles?: BurdenParticleEntry[];
  pathRuns?: Array<{ status?: string; derivedParticleIds?: string[] | null }>;
  /** 列表接口：是否已有真正生成的品题浓缩结果 */
  hasCompleteGeneratedIpttq?: boolean;
  /** 详情接口可能带回；列表通常为空，类型放宽以兼容 TtqSession.ipttqRecords */
  ipttqRecords?: unknown;
};

function collectDerivedParticleIds(
  pathRuns?: Array<{ status?: string; derivedParticleIds?: string[] | null }>,
): Set<string> {
  const derivedIds = new Set<string>();
  for (const run of pathRuns ?? []) {
    if (run.status && run.status !== 'done') continue;
    for (const id of run.derivedParticleIds ?? []) {
      const trimmed = String(id || '').trim();
      if (trimmed) derivedIds.add(trimmed);
    }
  }
  return derivedIds;
}

function listOriginalParticleIds(session: BurdenSessionSource): string[] {
  const particles = session.particles ?? [];
  const derivedIds = collectDerivedParticleIds(session.pathRuns);
  const out: string[] = [];
  for (const entry of particles) {
    const id = String(entry.particleId || '').trim();
    if (!id || derivedIds.has(id)) continue;
    out.push(id);
  }
  return out;
}

function readIpttqFallbackRecords(raw: unknown): Array<{
  bankMode?: string | null;
  lifecycle?: string | null;
  skill?: {
    implicitCapabilities?: string[] | null;
    thinkingLineage?: string | null;
  } | null;
  bankParticleIds?: string[] | null;
  effectiveBankCount?: number | null;
}> {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is Record<string, unknown> => item != null && typeof item === 'object',
  ) as Array<{
    bankMode?: string | null;
    lifecycle?: string | null;
    skill?: {
      implicitCapabilities?: string[] | null;
      thinkingLineage?: string | null;
    } | null;
    bankParticleIds?: string[] | null;
    effectiveBankCount?: number | null;
  }>;
}

function sessionHasCompleteGeneratedIpttq(session: BurdenSessionSource): boolean {
  if (typeof session.hasCompleteGeneratedIpttq === 'boolean') {
    return session.hasCompleteGeneratedIpttq;
  }
  const records = readIpttqFallbackRecords(session.ipttqRecords);
  return records.some((record) => {
    if (record.bankMode === 'session_originals_ref') return false;
    if (record.lifecycle === 'archived') return false;
    const caps = record.skill?.implicitCapabilities || [];
    const lineage = String(record.skill?.thinkingLineage || '').trim();
    const draftLineage = !lineage
      || lineage.includes('待根据')
      || lineage.includes('请稍后重试');
    const hasSkillBody = caps.length > 0 || (!draftLineage && lineage.length >= 12);
    if (!hasSkillBody) return false;
    const bankCount = typeof record.effectiveBankCount === 'number'
      ? record.effectiveBankCount
      : (record.bankParticleIds || []).length;
    return record.lifecycle === 'active' || bankCount > 0 || caps.length > 0;
  });
}

/**
 * 跨品题会话去重后的原始知识颗粒数。
 * 默认仅计入「已有真正品题浓缩结果」的会话（空壳草稿 / 平凡引用不参与负担统计）。
 */
export function countUniqueOriginalParticles(
  sessions: BurdenSessionSource[],
  options?: { requireCompleteIpttq?: boolean },
): number {
  const requireComplete = options?.requireCompleteIpttq !== false;
  const ids = new Set<string>();
  for (const session of sessions) {
    if (requireComplete && !sessionHasCompleteGeneratedIpttq(session)) continue;
    for (const id of listOriginalParticleIds(session)) {
      ids.add(id);
    }
  }
  return ids.size;
}

/**
 * 复习负担减少比例（展示用）：
 * 去重原始 KP（已被真正浓缩结果覆盖） / 当前学科全部 KP
 */
export function computeReviewBurdenReductionPercent(
  coveredOriginalCount: number,
  subjectParticleTotal: number | null,
): number | null {
  if (subjectParticleTotal == null || subjectParticleTotal <= 0) return null;
  if (coveredOriginalCount <= 0) return 0;
  return Math.min(100, Math.round((coveredOriginalCount / subjectParticleTotal) * 1000) / 10);
}

/** 尚未被真正浓缩结果覆盖的学科颗粒数 */
export function computeUnusedParticleCount(
  coveredOriginalCount: number,
  subjectParticleTotal: number | null,
): number | null {
  if (subjectParticleTotal == null || subjectParticleTotal <= 0) return null;
  return Math.max(0, subjectParticleTotal - Math.max(0, coveredOriginalCount));
}

/**
 * 若剩余颗粒全部完成真正品题浓缩，复习负担大约再减少的比例：
 * (M − N) / M = 1 − N/M
 */
export function computeProjectedRemainingBurdenReductionPercent(
  coveredOriginalCount: number,
  subjectParticleTotal: number | null,
): number | null {
  const unused = computeUnusedParticleCount(coveredOriginalCount, subjectParticleTotal);
  if (unused == null || subjectParticleTotal == null) return null;
  return computeReviewBurdenReductionPercent(unused, subjectParticleTotal);
}
